/* eslint-disable no-unused-vars */
import React from 'react';
import Button from 'components/common/layout/Button';
import { TextArea } from 'components/common/Form';
import { deepMerge, getLeafPaths, setAtPath } from '../utils';

const styles = {
  error: { color: '#d32f2f', fontSize: 11, marginTop: 4 },
  textarea: { fontFamily: 'monospace', fontSize: 12, minHeight: 220 },
  container: { position: 'fixed', bottom: 0, right: 0, zIndex: 9999 },
  sectionLabel: { fontSize: 11, color: '#888', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 },
  keysWrapper: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10, maxHeight: 350, overflowY: 'auto' },
  chip: { cursor: 'pointer', background: '#f0f0f0', padding: '2px 6px', borderRadius: 3, fontSize: 11, fontFamily: 'monospace', userSelect: 'none' },
  toggle: { display: 'block', marginLeft: 'auto', padding: '4px 12px', background: '#333', color: '#fff', cursor: 'pointer', border: 'none', borderRadius: '4px 0 0 0', fontSize: 12 },
  panel: { width: 400, height: 800, background: '#fff', border: '1px solid #ddd', padding: 12, boxShadow: '0 -2px 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' },
};

const OverwriteDrawer = ({ originalData, onApply, onReset }) => {
  const [value, setValue] = React.useState('');
  const [filter, setFilter] = React.useState('');
  const [error, setError] = React.useState(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const handleChange = (newValue) => {
    const localValue = newValue?.target?.value;
    setValue(localValue);
    if (!localValue?.trim()) {
      setError(null);
      return;
    }
    try {
      JSON.parse(localValue);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleConfirm = () => {
    if (value.trim()) {
      try {
        const parsed = JSON.parse(value);
        setError(null);
        onApply(parsed);
      } catch (e) {
        setError(e.message);
      }
    }
  };

  const handleReset = () => {
    setValue('');
    setError(null);
    onReset();
  };

  const handleKeyChipClick = (path, leafValue) => {
    let current = {};
    try {
      if (value?.trim()) {
        current = JSON.parse(value);
      }
    // eslint-disable-next-line no-empty
    } catch (_) {}
    const insertValue = leafValue;
    const newValue = JSON.stringify(deepMerge(current, setAtPath(path, insertValue)), null, 2);
    setValue(newValue);
    try {
      JSON.parse(newValue);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  };

  const leafPaths = React.useMemo(
    () => (originalData ? getLeafPaths(originalData).filter(({ path }) => !path.includes('translations')) : []),
    [originalData]
  );

  const visiblePaths = React.useMemo(
    () => (filter.trim()
      ? leafPaths.filter(({ path }) => path.includes(filter.trim()))
      : leafPaths),
    [leafPaths, filter]
  );

  const handleOnToggleOpen = () => {
    setIsOpen(o => !o);
    setFilter('');
  };

  return (
    <div style={styles.container}>
      <Button style={styles.toggle} onClick={handleOnToggleOpen}>
        {isOpen ? '❌ Close' : '⚙️ Override data'}
      </Button>
      {isOpen && (
        <div style={styles.panel}>
          <div>
            <div style={styles.sectionLabel}>Keys</div>
            <input
              style={{ width: '100%', marginBottom: 6, padding: '3px 6px', fontSize: 11, fontFamily: 'monospace', boxSizing: 'border-box' }}
              placeholder="Filter keys..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
            <div style={styles.keysWrapper}>
              {visiblePaths.map(({ path, value: leafValue }) => (
                <span
                  key={path}
                  style={styles.chip}
                  onClick={() => handleKeyChipClick(path, leafValue)}
                >
                  {path}
                </span>
              ))}
            </div>
          </div>
          <TextArea
            className="m-b-1"
            name="json-override"
            value={value}
            onChange={handleChange}
            style={styles.textarea}
          />
          {error && <div style={styles.error}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <Button onClick={handleConfirm} disabled={!!error || !value.trim()}>Confirm</Button>
            <Button onClick={handleReset}>Reset</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverwriteDrawer;
